import React, { useState, useEffect } from "react";
import {
  Clock, Package, ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  TrendingUp, TrendingDown, Minus, BarChart3
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * Simplified job cost summary.
 *
 * Design goals (May 2026 redesign):
 *   - ONE hero number: "You kept $X" — the bottom line, color-coded.
 *   - ONE health pill: ON TRACK / WATCH / OVER BUDGET (green / yellow / red).
 *   - Plain-language micro-rows for revenue / spent / kept.
 *   - Two progress bars (Labor / Materials) — at-a-glance.
 *   - Full table tucked behind a "Show details" toggle for power users.
 *
 * No jargon ("variance", "margin", "overhead") above the fold.
 */
export function JobCostSummary({ job, apiClient }) {
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (job?.id) fetchCostSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  const fetchCostSummary = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/jobs/${job.id}/cost-summary`);
      setCostData(res.data);
    } catch (err) {
      console.error('Failed to fetch cost summary:', err);
      setCostData(null);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8" data-testid="cost-summary-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!costData) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
        <p>Unable to load cost summary</p>
      </div>
    );
  }

  const { estimated, actual, variance, job_revenue, breakdown } = costData;
  const revenue = job_revenue || 0;
  const spent = actual.total_cost || 0;
  const kept = actual.profit || 0;
  const marginPct = actual.profit_margin || 0;

  const fmtMoney = (val) => {
    if (val === undefined || val === null) return '$0';
    return `$${val.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };
  const fmtPercent = (val) => {
    if (val === undefined || val === null) return '0%';
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(1)}%`;
  };

  // Compute a single "health" status from the variance and profit data.
  // Drives both the hero color and the badge. Thresholds tuned for service-
  // business reality — landing within 10% of estimate is normal, 10-25% over
  // is a yellow flag, 25%+ over is red.
  const variancePct = variance?.total_cost_percent || 0;
  let health = 'on_track';
  if (kept < 0) {
    health = 'over_budget';
  } else if (variancePct > 25) {
    health = 'over_budget';
  } else if (variancePct > 10) {
    health = 'watch';
  }

  const HEALTH_CONFIG = {
    on_track: {
      label: 'On Track',
      desc: 'Costs are in line with your estimate.',
      ring: 'bg-emerald-500/10 border-emerald-500/40',
      pill: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
      text: 'text-emerald-300',
      icon: CheckCircle,
    },
    watch: {
      label: 'Keep an Eye',
      desc: "You're running a little over — still profitable, but watch the rest.",
      ring: 'bg-amber-500/10 border-amber-500/40',
      pill: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
      text: 'text-amber-300',
      icon: AlertTriangle,
    },
    over_budget: {
      label: 'Over Budget',
      desc: 'Costs have run past the estimate. Review before invoicing.',
      ring: 'bg-rose-500/10 border-rose-500/40',
      pill: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
      text: 'text-rose-300',
      icon: AlertTriangle,
    },
  };
  const h = HEALTH_CONFIG[health];
  const HIcon = h.icon;

  // Trend chip for "what you keep" — up/down vs the estimated profit.
  const profitDelta = (actual.profit || 0) - (estimated.profit || 0);
  const TrendIcon = profitDelta > 1 ? TrendingUp : profitDelta < -1 ? TrendingDown : Minus;
  const trendColor = profitDelta > 1 ? 'text-emerald-300' : profitDelta < -1 ? 'text-rose-300' : 'text-zinc-400';

  // Progress bar helper — capped at 100% visually but the label always shows
  // the real percentage so admins know just how far over they are.
  const Progress = ({ label, used, budgeted, color }) => {
    const pct = budgeted > 0 ? (used / budgeted) * 100 : 0;
    const overBudget = used > budgeted && budgeted > 0;
    return (
      <div>
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-zinc-300">{label}</span>
          <span className={overBudget ? 'text-rose-300 font-medium' : 'text-zinc-400'}>
            {fmtMoney(used)} of {fmtMoney(budgeted)} ({pct.toFixed(0)}%)
          </span>
        </div>
        <div className="w-full bg-zinc-800 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full transition-all ${overBudget ? 'bg-rose-500' : color}`}
            style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="job-cost-summary">
      {/* HERO: one big number + health pill. This is what the admin should
          see and understand in under 2 seconds. Everything else is supporting. */}
      <div className={`p-5 rounded-xl border ${h.ring}`} data-testid="cost-summary-hero">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-zinc-400 mb-1">You kept</p>
            <p className={`text-4xl sm:text-5xl font-bold ${h.text}`} data-testid="cost-summary-profit">
              {fmtMoney(kept)}
            </p>
            <p className="text-sm text-zinc-400 mt-1">
              That's <span className="text-zinc-200 font-medium">{marginPct.toFixed(0)}¢</span> from every dollar you charged.
            </p>
          </div>
          <Badge className={`${h.pill} text-xs uppercase tracking-wider px-3 py-1.5 self-start flex items-center gap-1.5`} data-testid="cost-summary-health-pill">
            <HIcon className="w-3.5 h-3.5" />
            {h.label}
          </Badge>
        </div>
        <p className="text-sm text-zinc-400 mt-3">{h.desc}</p>
      </div>

      {/* MICRO-ROWS: plain-language summary. Three rows, color hint on the
          profit row. No jargon. */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4 space-y-2.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-400">What you charged</span>
            <span className="text-white font-medium tabular-nums">{fmtMoney(revenue)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-400">What you spent</span>
            <span className="text-white font-medium tabular-nums">{fmtMoney(spent)}</span>
          </div>
          <div className="h-px bg-zinc-800" />
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-300 font-medium">What you keep</span>
            <span className={`font-bold tabular-nums ${kept >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
              {fmtMoney(kept)}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs pt-1">
            <span className="text-zinc-500">vs. your estimate</span>
            <span className={`flex items-center gap-1 ${trendColor}`}>
              <TrendIcon className="w-3 h-3" />
              {profitDelta >= 0 ? '+' : ''}{fmtMoney(profitDelta)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* PROGRESS BARS: at-a-glance budget usage. */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4 space-y-3.5">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider text-zinc-400">Budget usage</p>
            <span className="text-[10px] text-zinc-500">used / planned</span>
          </div>
          <Progress
            label={(<span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-blue-400" /> Labor</span>)}
            used={actual.labor_cost || 0}
            budgeted={estimated.labor_cost || 0}
            color="bg-blue-500"
          />
          <Progress
            label={(<span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-orange-400" /> Materials</span>)}
            used={actual.total_material_cost || actual.material_cost || 0}
            budgeted={estimated.material_cost || 0}
            color="bg-orange-500"
          />
        </CardContent>
      </Card>

      {/* Show details — collapsed by default. */}
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="w-full flex items-center justify-center gap-2 py-2 text-xs text-zinc-400 hover:text-white transition-colors"
        data-testid="cost-summary-toggle-details"
      >
        {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {showDetails ? 'Hide details' : 'Show full breakdown'}
      </button>

      {showDetails && (
        <Card className="bg-zinc-900 border-zinc-800" data-testid="cost-summary-details">
          <CardContent className="p-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-2 text-xs text-zinc-500 uppercase font-medium">Category</th>
                  <th className="text-right py-2 text-xs text-zinc-500 uppercase font-medium">Estimated</th>
                  <th className="text-right py-2 text-xs text-zinc-500 uppercase font-medium">Actual</th>
                  <th className="text-right py-2 text-xs text-zinc-500 uppercase font-medium">Diff</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-zinc-800/50">
                  <td className="py-2.5 flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-blue-400" /><span className="text-zinc-300">Labor hours</span></td>
                  <td className="text-right text-zinc-400 tabular-nums">{(estimated.labor_hours || 0).toFixed(1)}h</td>
                  <td className="text-right text-white font-medium tabular-nums">{(actual.labor_hours || 0).toFixed(1)}h</td>
                  <td className="text-right">
                    <span className={(variance.labor_hours || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'}>
                      {(variance.labor_hours || 0) > 0 ? '+' : ''}{(variance.labor_hours || 0).toFixed(1)}h
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-zinc-800/50">
                  <td className="py-2.5"><span className="text-zinc-300 pl-5">Labor $</span></td>
                  <td className="text-right text-zinc-400 tabular-nums">{fmtMoney(estimated.labor_cost)}</td>
                  <td className="text-right text-white font-medium tabular-nums">{fmtMoney(actual.labor_cost)}</td>
                  <td className="text-right">
                    <span className={(variance.labor_cost || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'}>
                      {fmtPercent(variance.labor_cost_percent)}
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-zinc-800/50">
                  <td className="py-2.5 flex items-center gap-2"><Package className="w-3.5 h-3.5 text-orange-400" /><span className="text-zinc-300">Materials</span></td>
                  <td className="text-right text-zinc-400 tabular-nums">{fmtMoney(estimated.material_cost)}</td>
                  <td className="text-right text-white font-medium tabular-nums">{fmtMoney(actual.material_cost)}</td>
                  <td className="text-right">
                    <span className={(variance.material_cost || 0) > 0 ? 'text-rose-300' : 'text-emerald-300'}>
                      {fmtPercent(variance.material_cost_percent)}
                    </span>
                  </td>
                </tr>
                {(actual.additional_material_cost || 0) > 0 && (
                  <tr className="border-b border-zinc-800/50 bg-orange-900/10">
                    <td className="py-2.5"><span className="text-orange-300 pl-7 text-xs">+ added during job</span></td>
                    <td className="text-right text-zinc-600 tabular-nums">—</td>
                    <td className="text-right text-orange-300 font-medium tabular-nums">{fmtMoney(actual.additional_material_cost)}</td>
                    <td className="text-right"><Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40 text-[10px]">added</Badge></td>
                  </tr>
                )}
                <tr className="bg-zinc-800/50">
                  <td className="py-2.5 font-bold text-white">Total cost</td>
                  <td className="text-right text-zinc-300 font-bold tabular-nums">{fmtMoney(estimated.total_cost)}</td>
                  <td className="text-right text-white font-bold tabular-nums">{fmtMoney(actual.total_cost)}</td>
                  <td className="text-right">
                    <span className={`font-bold ${variancePct > 0 ? 'text-rose-300' : 'text-emerald-300'}`}>
                      {fmtPercent(variancePct)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="mt-3 pt-3 border-t border-zinc-800 flex flex-wrap gap-4 text-xs text-zinc-500">
              <span>{breakdown.time_entries_count} time entries</span>
              <span>{breakdown.material_usage_count} material rows</span>
              {breakdown.additional_materials_count > 0 && (
                <span className="text-orange-300">{breakdown.additional_materials_count} added items</span>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default JobCostSummary;
